// The "Payment details" fields on the subcontractor's own secure pages (Session ARZ): the registration form and the
// payment-details-only link. One component, so both pages ask the same way and say the same things.
//
// Plain English for somebody with no banking or system knowledge: why ELE needs it (to pay their invoices), that it is
// stored securely, that ELE will phone to confirm before the first payment, and that ELE never asks for bank details by
// email. The numbers are typed into plain text inputs with autocomplete off, never echoed anywhere else on the page, and
// leave the browser only in the form's own POST.
//
// Styled for the light, customer-facing shell (SubcontractorPages.css / .subpage): these fields sit inside a
// customer's own page, not the internal office/engineer app, so they use the same light-canvas classes those
// pages use, never the app shell's dark bg-ele-* tokens.
import type { ReactNode } from "react";
import type { PaymentDetailsField, PaymentDetailsForm } from "../../lib/subcontractorPayments";

const PHONE = "0203 576 4760";

export function PaymentDetailsIntro() {
  return (
    <>
      <p className="mb-2 text-sm" style={{ color: "var(--sp-ink-muted)" }}>
        We need your bank details so we can pay your invoices. They are stored securely and encrypted. Our screens show
        only the last few digits, and the full details can be viewed only by a senior member of staff, with every view
        recorded.
      </p>
      <p className="mb-3 text-sm" style={{ color: "var(--sp-ink-muted)" }}>
        Before we make your first payment we will phone you to confirm them. We will never ask you to send your bank
        details by email. If you ever receive an email that seems to come from us asking you to change them, please call
        us on {PHONE} before doing anything.
      </p>
    </>
  );
}

export function PaymentDetailsFields({
  value,
  onChange,
  errors,
}: {
  value: PaymentDetailsForm;
  onChange: (next: PaymentDetailsForm) => void;
  errors: Partial<Record<PaymentDetailsField, string>>;
}) {
  const set = (k: PaymentDetailsField) => (v: string) => onChange({ ...value, [k]: v });
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="sm:col-span-2">
        <Input label="Name on the account" value={value.accountName} onChange={set("accountName")} error={errors.accountName} autoComplete="off" />
      </div>
      <Input label="Sort code" value={value.sortCode} onChange={set("sortCode")} error={errors.sortCode} placeholder="12-34-56" inputMode="numeric" />
      <div />
      <Input
        label="Account number"
        value={value.accountNumber}
        onChange={set("accountNumber")}
        error={errors.accountNumber}
        placeholder="8 digits"
        inputMode="numeric"
      />
      <Input
        label="Account number again"
        value={value.accountNumberConfirm}
        onChange={set("accountNumberConfirm")}
        error={errors.accountNumberConfirm}
        placeholder="To check it is right"
        inputMode="numeric"
      />
    </div>
  );
}

function Input({
  label,
  value,
  onChange,
  error,
  placeholder,
  inputMode,
  autoComplete = "off",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  placeholder?: string;
  inputMode?: "numeric";
  autoComplete?: string;
}) {
  return (
    <div>
      <label className="sp-label">{label} *</label>
      <input
        type="text"
        value={value}
        inputMode={inputMode}
        autoComplete={autoComplete}
        spellCheck={false}
        placeholder={placeholder}
        aria-label={label}
        aria-invalid={error ? true : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
      {error && <FieldError>{error}</FieldError>}
    </div>
  );
}

function FieldError({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="sp-error-text">
      {children}
    </p>
  );
}
